import { useAuth } from './useAuth';

interface PermissionQuery {
  module: string;
  feature: string;
}

/**
 * Synchronous permission check against cached permissions from GET /api/v1/admin/users/current.
 * Returns true if the user has access to the specified module/feature.
 * Returns true if permissions haven't loaded yet (optimistic — backend enforces).
 */
export function usePermissionCheck(query: PermissionQuery): boolean {
  const { permissions, isAdminRole } = useAuth();
  if (isAdminRole) return true;
  if (!permissions?.actions) return true;
  return permissions.actions.some(
    (a) => a.module === query.module && a.feature === query.feature && a.hasAccess
  );
}

/**
 * Check if a module is available (AVAILABILITY constraint).
 * Checks if ANY feature in the module has access.
 */
export function useModuleAvailable(moduleCode: string): boolean {
  const { permissions, isAdminRole } = useAuth();
  if (isAdminRole) return true;
  if (!permissions?.actions) return true;
  return permissions.actions.some(
    (a) => a.module === moduleCode && a.hasAccess
  );
}

/**
 * Check multiple permissions at once. Returns true if ALL are granted.
 */
export function usePermissionCheckAll(queries: PermissionQuery[]): boolean {
  const { permissions, isAdminRole } = useAuth();
  if (isAdminRole) return true;
  if (!permissions?.actions) return true;
  return queries.every((q) =>
    permissions.actions.some(
      (a) => a.module === q.module && a.feature === q.feature && a.hasAccess
    )
  );
}

/**
 * Check multiple permissions. Returns true if ANY is granted.
 */
export function usePermissionCheckAny(queries: PermissionQuery[]): boolean {
  const { permissions, isAdminRole } = useAuth();
  if (isAdminRole) return true;
  if (!permissions?.actions) return true;
  return queries.some((q) =>
    permissions.actions.some(
      (a) => a.module === q.module && a.feature === q.feature && a.hasAccess
    )
  );
}
